import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../../lib/axios";

type ZoneDetail = {
  id: number;
  code?: string | null;
  nom: string;
  description?: string | null;
  province?: string | null;
  ville?: string | null;
  communes?: { id: number; nom: string }[];
  quartierLibre?: string | null;
  quartierNom?: string | null;
  status?: string;
  riskLevel?: string;
  slaPriority?: string;
  dailyCapacity?: number | null;
  maxTeams?: number | null;
  averagePassageMin?: number | null;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  responsable?: { nom: string; prenom?: string | null } | null;
  notes?: string | null;
};

type Summary = {
  zoneId: number;
  clientsActive: number;
  passagesToday: number;
  activeTournees: number;
  latePassages: number;
  incidents: number;
  capacityUsed: number;
  capacityTotal: number | null;
  gpsCoverageStatus: string;
};

export default function ZoneDetailPage() {
  const { id } = useParams();
  const [zone, setZone] = useState<ZoneDetail | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [zoneRes, summaryRes] = await Promise.all([
        api.get(`/zones/${id}`),
        api.get("/zones/operational-summary").catch(() => ({ data: { data: [] } })),
      ]);
      setZone(zoneRes.data);
      const rows = Array.isArray(summaryRes.data?.data) ? summaryRes.data.data : [];
      setSummary(rows.find((row: Summary) => String(row.zoneId) === String(id)) ?? null);
      setLoading(false);
    }
    void load();
  }, [id]);

  if (loading) return <div className="min-h-screen bg-slate-950 p-8 text-slate-300">Chargement...</div>;
  if (!zone) return <div className="min-h-screen bg-slate-950 p-8 text-slate-300">Zone introuvable.</div>;

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-6 text-slate-100 md:px-8">
      <Link to="/admin/zones" className="text-sm text-sky-300">Retour aux zones</Link>
      <header className="mt-4 rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
        <p className="font-mono text-sm text-sky-200">{zone.code || "Code non défini"}</p>
        <h1 className="mt-1 text-2xl font-bold text-white">{zone.nom}</h1>
        <p className="mt-1 text-sm text-slate-400">{zone.description || "Référentiel terrain opérationnel."}</p>
      </header>

      <section className="mt-5 grid gap-3 md:grid-cols-4">
        <Card label="Clients actifs" value={summary?.clientsActive ?? 0} />
        <Card label="Passages du jour" value={summary?.passagesToday ?? 0} />
        <Card label="Tournées actives" value={summary?.activeTournees ?? 0} />
        <Card label="Retards" value={summary?.latePassages ?? 0} />
      </section>

      <section className="mt-5 grid gap-4 lg:grid-cols-2">
        <Panel title="Résumé zone">
          <Line label="Statut" value={zone.status ?? "ACTIVE"} />
          <Line label="Risque" value={zone.riskLevel ?? "LOW"} />
          <Line label="Priorité SLA" value={zone.slaPriority ?? "STANDARD"} />
          <Line label="Responsable" value={zone.responsable ? `${zone.responsable.nom} ${zone.responsable.prenom ?? ""}`.trim() : "—"} />
        </Panel>
        <Panel title="Couverture géographique">
          <Line label="Province / ville" value={`${zone.province ?? "—"} / ${zone.ville ?? "—"}`} />
          <Line label="Commune" value={zone.communes?.map((c) => c.nom).join(", ") || "—"} />
          <Line label="Quartier" value={zone.quartierLibre ?? zone.quartierNom ?? "—"} />
          <Line label="Centre GPS" value={zone.centerLatitude != null && zone.centerLongitude != null ? `${zone.centerLatitude}, ${zone.centerLongitude}` : "Non défini"} />
        </Panel>
        <Panel title="Capacité et SLA">
          <Line label="Capacité utilisée" value={summary?.capacityTotal ? `${summary.capacityUsed}/${summary.capacityTotal}` : "Non définie"} />
          <Line label="Nombre max équipes" value={zone.maxTeams ?? "—"} />
          <Line label="Temps moyen passage" value={zone.averagePassageMin ? `${zone.averagePassageMin} min` : "—"} />
          <Line label="Incidents" value={summary?.incidents ?? 0} />
        </Panel>
        <Panel title="Sections opérationnelles préparées">
          <p className="text-sm text-slate-400">Clients de la zone, équipes affectées, tournées actives, passages du jour, retards, incidents, historique événements et ressources liées seront consolidés ici.</p>
        </Panel>
      </section>
    </div>
  );
}

function Card({ label, value }: { label: string; value: number }) {
  return <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4"><p className="text-xs text-slate-500">{label}</p><p className="mt-1 text-2xl font-bold text-white">{value}</p></div>;
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5"><h2 className="text-lg font-semibold text-white">{title}</h2><div className="mt-3 space-y-2">{children}</div></div>;
}

function Line({ label, value }: { label: string; value: ReactNode }) {
  return <div className="flex justify-between gap-4 border-b border-slate-800/70 pb-2 text-sm"><span className="text-slate-500">{label}</span><span className="text-right text-slate-200">{value}</span></div>;
}
